import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, Bars, Segmented, TableWrap, Th, Td, UserLink, MarketTag } from '../components/ui/bits';
import { DailySplit } from '../components/charts/DailySplit';
import { useRpc } from '../lib/queries';
import { agoLabel, change, fmtPct, fmtDay } from '../lib/format';
import { sum } from '../lib/market';
import { CHANNEL, channelLabel, selfLabel } from '../lib/labels';
import { sumMetric, type DailyMetric, type FunnelRow, type GrowthResp } from '../lib/types';

const STEPS: { key: keyof FunnelRow; label: string; note: string }[] = [
  { key: 'signed_up', label: 'Signed up', note: 'auth.users' },
  { key: 'onboarded', label: 'Finished onboarding', note: 'profiles.onboarding_completed' },
  { key: 'reached_feature', label: 'Used a feature', note: 'Any session, psychometric run, tool or Vyo message' },
  { key: 'saw_paywall', label: 'Saw a paywall', note: 'paywall_viewed event, recorded from 29 Oct' },
  { key: 'started_checkout', label: 'Started checkout', note: 'checkout_started (from 29 Oct) or the older upgrade_checkout_started' },
  { key: 'paid', label: 'Paid', note: 'Stripe subscription id, a pack with a Stripe payment, or purchase_completed' },
];

const DAILY_STEPS: [keyof DailyMetric, string][] = [
  ['visitors', 'Visitors'],
  ['signups', 'Signed up'],
  ['onboarded', 'Onboarded'],
  ['first_feature', 'First feature'],
  ['paywall_viewers', 'Saw a paywall'],
  ['checkout_starters', 'Started checkout'],
  ['stripe_sessions', 'Stripe session'],
  ['purchases', 'Purchased'],
];

const FunnelTable: React.FC<{ f: GrowthResp['funnel'] }> = ({ f }) => (
  <TableWrap>
    <thead>
      <tr>
        <Th>Step</Th>
        <Th className="text-right">UK</Th><Th className="text-right">UK % of signups</Th>
        <Th className="text-right">US</Th><Th className="text-right">US % of signups</Th>
        <Th>Counted from</Th>
      </tr>
    </thead>
    <tbody>
      {STEPS.map(s => (
        <tr key={s.key}>
          <Td className="font-medium text-stone-900 whitespace-nowrap">{s.label}</Td>
          <Td className="text-right tabular-nums">{f.uk[s.key].toLocaleString('en-GB')}</Td>
          <Td className="text-right tabular-nums text-stone-500">{fmtPct(f.uk[s.key], f.uk.signed_up)}</Td>
          <Td className="text-right tabular-nums">{f.us[s.key].toLocaleString('en-GB')}</Td>
          <Td className="text-right tabular-nums text-stone-500">{fmtPct(f.us[s.key], f.us.signed_up)}</Td>
          <Td className="text-[12px] text-stone-500">{s.note}</Td>
        </tr>
      ))}
    </tbody>
  </TableWrap>
);

/** Signups by London day, where they came from, and how far they get. Internal accounts excluded everywhere. */
export const GrowthPage: React.FC = () => {
  const [days, setDays] = useState('30');
  const { data: g, error, isLoading } = useRpc<GrowthResp>('admin_dash_growth', { p_days: Number(days) });
  const dm = useRpc<DailyMetric[]>('admin_daily_metrics', { p_days: Number(days) });

  const cross = useMemo(() => {
    if (!g) return null;
    const selfTop = g.self_reported.filter(r => r.source !== 'not_answered').slice(0, 6).map(r => r.source);
    const chanTop = g.attribution.slice(0, 6).map(r => r.channel);
    const cell = (s: string, c: string) => g.cross.find(x => x.self_source === s && x.channel === c)?.n ?? 0;
    return { selfTop, chanTop, cell };
  }, [g]);

  if (isLoading) return <LoadingRow label="Loading growth" />;
  if (error || !g) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_growth" />;

  const win = sum(g.totals.window);
  const answered = g.self_reported.filter(r => r.source !== 'not_answered').reduce((a, r) => a + r.signups, 0);
  const captured = g.attribution.filter(r => !['not_captured', 'own_site'].includes(r.channel)).reduce((a, r) => a + r.signups, 0);
  const paid = g.funnel.uk.paid + g.funnel.us.paid;
  const d1 = sum(g.active.d1), d30 = sum(g.active.d30);

  return (
    <div>
      <PageHeader
        title="Growth"
        subtitle="Signups by London day, where people say they came from, what the browser recorded, and how far they get. Internal accounts are excluded."
        actions={<Segmented label="Window" value={days} onChange={setDays} options={[
          { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: '1 year' },
        ]} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <StatTile label={`Signups, ${g.days} days`} value={win.toLocaleString('en-GB')} split={g.totals.window} sub={`${(win / g.days).toFixed(1)} a day`} source="auth.users, London days" />
        <StatTile label="Signups, 7 days" value={sum(g.totals.d7)} split={g.totals.d7} delta={change(sum(g.totals.d7), sum(g.totals.prev7))} sub={`${sum(g.totals.prev7)} the week before`} />
        <StatTile label="All signups" value={sum(g.totals.all_time).toLocaleString('en-GB')} split={g.totals.all_time} />
        <StatTile label="Active, 7 days" value={sum(g.active.d7)} split={g.active.d7} sub={`${d1} today, ${d30} in 30 days`} source="Sessions, runs, Vyo, tools, non-passive events" />
        <StatTile label="Daily over monthly active" value={fmtPct(d1, d30, 0)} sub="How often monthly users come back" />
        <StatTile label={`Paid, of ${g.days}-day signups`} value={paid} split={{ uk: g.funnel.uk.paid, us: g.funnel.us.paid }} sub={fmtPct(paid, win) + ' of signups'} source="Stripe-linked, never comps" />
      </div>

      <Section title="Signups per day" source="auth.users created_at, Europe/London" className="mb-3">
        <DailySplit data={g.daily} unit="signups" />
      </Section>

      <Section
        title="Daily funnel"
        sub="What happened on each London day, as events: visitors, then people reaching each step that day. Visitors are marketing-site visitors. Steps after signup are recorded from 28 to 29 Oct onwards, so earlier days show zeros there."
        source="admin_daily_metrics (live tracking view, internal accounts excluded)"
        className="mb-3"
      >
        {dm.error ? <ErrorPanel error={dm.error} label="admin_daily_metrics" /> : dm.isLoading ? <LoadingRow /> : (
          <>
            <TableWrap>
              <thead><tr><Th>{`Step, ${g.days} days`}</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th><Th className="text-right">Total</Th></tr></thead>
              <tbody>
                {DAILY_STEPS.map(([k, label]) => (
                  <tr key={k}>
                    <Td className="font-medium text-stone-900">{label}</Td>
                    <Td className="text-right tabular-nums">{sumMetric(dm.data, k, 'uk').toLocaleString('en-GB')}</Td>
                    <Td className="text-right tabular-nums">{sumMetric(dm.data, k, 'us').toLocaleString('en-GB')}</Td>
                    <Td className="text-right tabular-nums text-stone-500">{sumMetric(dm.data, k).toLocaleString('en-GB')}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <details className="mt-3">
              <summary className="cursor-pointer text-[12.5px] font-medium text-indigo-600">Day by day</summary>
              <div className="mt-2 max-h-[380px] overflow-y-auto">
                <TableWrap>
                  <thead><tr><Th>Day</Th><Th>Market</Th>{DAILY_STEPS.map(([k, label]) => <Th key={k} className="text-right">{label}</Th>)}</tr></thead>
                  <tbody>
                    {[...(dm.data ?? [])].sort((a, b) => b.day.localeCompare(a.day) || a.market.localeCompare(b.market)).map(r => (
                      <tr key={`${r.day}-${r.market}`}>
                        <Td className="whitespace-nowrap">{fmtDay(r.day)}</Td>
                        <Td><MarketTag market={r.market} /></Td>
                        {DAILY_STEPS.map(([k]) => <Td key={k} className="text-right tabular-nums">{Number(r[k]) || 0}</Td>)}
                      </tr>
                    ))}
                  </tbody>
                </TableWrap>
              </div>
            </details>
          </>
        )}
      </Section>

      <Section title="Signup funnel" sub={`Everyone who signed up in the last ${g.days} days, and how many reached each step since. Paywall and checkout steps only count from 29 Oct, when that tracking started (${g.funnel.uk.signed_up_since_tracking + g.funnel.us.signed_up_since_tracking} of these signups came after it).`} className="mb-3">
        <FunnelTable f={g.funnel} />
      </Section>

      <div className="grid xl:grid-cols-2 gap-3 mb-3">
        <Section
          title="Heard about us"
          sub={`What people chose in onboarding. ${answered.toLocaleString('en-GB')} of ${win.toLocaleString('en-GB')} answered (${fmtPct(answered, win, 0)}). This is the main source signal until first-touch capture is fixed.`}
          source="profiles.onboarding_referral_source, Other answers that name ChatGPT, Claude or AI grouped as AI assistant"
        >
          <TableWrap>
            <thead><tr><Th>Source</Th><Th className="text-right">Signups</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th><Th className="text-right">Onboarded</Th><Th className="text-right">Paid</Th><Th className="text-right">Paid %</Th></tr></thead>
            <tbody>
              {g.self_reported.map(r => (
                <tr key={r.source} className={r.source === 'not_answered' ? 'text-stone-400' : ''}>
                  <Td className={r.source === 'not_answered' ? 'text-stone-500' : 'font-medium text-stone-900'}>{selfLabel(r.source)}</Td>
                  <Td className="text-right tabular-nums">{r.signups.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{r.uk}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{r.us}</Td>
                  <Td className="text-right tabular-nums">{fmtPct(r.onboarded, r.signups, 0)}</Td>
                  <Td className="text-right tabular-nums">{r.paid}</Td>
                  <Td className="text-right tabular-nums">{fmtPct(r.paid, r.signups)}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {g.other_answers.length > 0 && (
            <p className="text-[12px] text-stone-500 mt-3">
              Typed under Other: {g.other_answers.slice(0, 10).map(a => `${a.answer} (${a.n})`).join(', ')}
            </p>
          )}
        </Section>

        <Section
          title="First-touch channel"
          sub={`What the browser recorded before signup. Only ${captured.toLocaleString('en-GB')} of ${win.toLocaleString('en-GB')} (${fmtPct(captured, win, 0)}) kept a real source; the rest were lost at the sign-in redirect, before cookie consent, or on the way from our own site.`}
          source="profiles.signup_attribution"
        >
          <TableWrap>
            <thead><tr><Th>Channel</Th><Th className="text-right">Signups</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th><Th className="text-right">Paid</Th></tr></thead>
            <tbody>
              {g.attribution.map(r => (
                <tr key={r.channel}>
                  <Td>
                    <span className={['not_captured', 'own_site'].includes(r.channel) ? 'text-stone-500' : 'font-medium text-stone-900'}>{channelLabel(r.channel)}</span>
                    {CHANNEL[r.channel]?.note && <div className="text-[12px] text-stone-400">{CHANNEL[r.channel]?.note}</div>}
                  </Td>
                  <Td className="text-right tabular-nums">{r.signups.toLocaleString('en-GB')}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{r.uk}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{r.us}</Td>
                  <Td className="text-right tabular-nums">{r.paid}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Section>
      </div>

      {cross && cross.selfTop.length > 0 && (
        <Section title="What people said, against what was recorded" sub="Rows are the onboarding answer, columns the first-touch channel. Shows how much of each real source the browser loses." source="profiles" className="mb-3">
          <TableWrap>
            <thead>
              <tr><Th>Said</Th>{cross.chanTop.map(c => <Th key={c} className="text-right">{channelLabel(c)}</Th>)}</tr>
            </thead>
            <tbody>
              {cross.selfTop.map(s => (
                <tr key={s}>
                  <Td className="font-medium text-stone-900">{selfLabel(s)}</Td>
                  {cross.chanTop.map(c => {
                    const v = cross.cell(s, c);
                    return <Td key={c} className={`text-right tabular-nums ${v ? '' : 'text-stone-300'}`}>{v}</Td>;
                  })}
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Section>
      )}

      <Section title="Weekly cohorts" sub="By signup week (Monday, London). Each column counts people from that week who reached the step at any time since." source="Same rules as the funnel" className="mb-3">
        {g.cohorts.length === 0 ? <EmptyNote>No signups in the window</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Week of</Th><Th className="text-right">Signed up</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th><Th className="text-right">Onboarded</Th><Th className="text-right">Used a feature</Th><Th className="text-right">Started checkout</Th><Th className="text-right">Paid</Th></tr></thead>
            <tbody>
              {g.cohorts.map(c => (
                <tr key={c.week}>
                  <Td className="whitespace-nowrap">{fmtDay(c.week)}</Td>
                  <Td className="text-right tabular-nums font-medium text-stone-900">{c.signed_up}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{c.uk}</Td>
                  <Td className="text-right tabular-nums text-stone-500">{c.us}</Td>
                  <Td className="text-right tabular-nums">{c.onboarded} <span className="text-stone-400">{fmtPct(c.onboarded, c.signed_up, 0)}</span></Td>
                  <Td className="text-right tabular-nums">{c.reached_feature} <span className="text-stone-400">{fmtPct(c.reached_feature, c.signed_up, 0)}</span></Td>
                  <Td className="text-right tabular-nums">{c.started_checkout} <span className="text-stone-400">{fmtPct(c.started_checkout, c.signed_up, 0)}</span></Td>
                  <Td className="text-right tabular-nums">{c.paid} <span className="text-stone-400">{fmtPct(c.paid, c.signed_up, 1)}</span></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>

      <div className="grid lg:grid-cols-3 gap-3 mb-3">
        <Section title="Universities" sub={`${g.universities_answered.toLocaleString('en-GB')} of ${win.toLocaleString('en-GB')} signups gave one.`} source="profiles.university">
          {g.universities.length === 0 ? <EmptyNote>None given</EmptyNote> : <Bars rows={g.universities.map(r => ({ key: r.label, label: r.label, n: r.n }))} />}
        </Section>
        <Section title="Target industry" sub={`${g.industries_answered.toLocaleString('en-GB')} answered.`} source="profiles.onboarding_industry">
          {g.industries.length === 0 ? <EmptyNote>None given</EmptyNote> : <Bars rows={g.industries.map(r => ({ key: r.label, label: r.label, n: r.n }))} />}
        </Section>
        <Section title="Marketing-site demos" sub={`${g.demos.run.toLocaleString('en-GB')} run, ${g.demos.claimed} claimed by an account (${fmtPct(g.demos.claimed, g.demos.run, 0)}).`} source="demo_sessions">
          {g.demos.by_feature.length === 0 ? <EmptyNote>No demos</EmptyNote> : <Bars rows={g.demos.by_feature.map(r => ({ key: r.feature, label: r.feature.replace(/_/g, ' '), n: r.n }))} />}
        </Section>
      </div>

      <Section title="Latest signups" sub="Newest 30 with both source signals." source="profiles">
        <TableWrap>
          <thead><tr><Th>User</Th><Th>Market</Th><Th>Heard about us</Th><Th>First touch</Th><Th>Landing page</Th><Th>Referrer</Th><Th>Paid</Th><Th>When</Th></tr></thead>
          <tbody>
            {g.recent.map(r => (
              <tr key={r.id}>
                <Td><UserLink id={r.id} /></Td>
                <Td><MarketTag market={r.market} /></Td>
                <Td>{selfLabel(r.self_source)}{r.self_other ? <span className="text-stone-500">: {r.self_other}</span> : null}</Td>
                <Td className="text-stone-600">{channelLabel(r.channel)}{r.campaign ? <div className="text-[12px] text-stone-500">{r.campaign}</div> : null}</Td>
                <Td className="text-[12px] text-stone-500 max-w-[180px] truncate">{r.landing_page ?? ''}</Td>
                <Td className="text-[12px] text-stone-500 max-w-[200px] truncate">{r.referrer ?? ''}</Td>
                <Td>{r.paid ? <Badge tone="emerald">Paid</Badge> : ''}</Td>
                <Td className="whitespace-nowrap">{agoLabel(r.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Section>
    </div>
  );
};
